import { useCallback } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { LocalDate } from "@js-joda/core";
import type {
  StaffProfileData,
  StaffProfileResponseV3,
} from "services/api/company/staff.service";
import type { ModalState, TranslateFn } from "../profilePanel.state.contract";
import {
  confirmEndEmploymentUpdate as runConfirmEndEmploymentUpdate,
  toSuggestedEndEmploymentDate,
} from "../profilePanel.state.endEmployment.logic";
import { EmploymentUiRules } from "../../../shared/staffEmploymentRules.logic";

interface UseProfilePanelEndEmploymentModalStateParams {
  companyId: number | null;
  selectedEmploymentStartDate: LocalDate | null;
  endEmploymentDate: LocalDate | null;
  employmentRules: EmploymentUiRules;
  isSaving: boolean;
  onWorkerUpdated?: () => void;
  profile: StaffProfileData | null;
  applySavedProfile: (savedProfile: StaffProfileResponseV3) => void;
  reloadProfile: () => void;
  setEndEmploymentDate: Dispatch<SetStateAction<LocalDate | null>>;
  setIsSaving: (value: boolean) => void;
  setModalState: Dispatch<SetStateAction<ModalState>>;
  setSaveError: (value: string) => void;
  translate: TranslateFn;
  workerId: number | null;
  onStartCreatingEmployment: () => void;
}

interface UseProfilePanelEndEmploymentModalStateResult {
  handleOpenEndEmploymentModal: () => void;
  openEndEmploymentForCreateModal: () => void;
  handleCloseEndEmploymentModal: () => void;
  handleConfirmEndEmploymentModal: () => void;
  handleCloseEndEmploymentForCreateModal: () => void;
  handleConfirmEndEmploymentForCreateModal: () => void;
}

export const useProfilePanelEndEmploymentModalState = (
  params: UseProfilePanelEndEmploymentModalStateParams,
): UseProfilePanelEndEmploymentModalStateResult => {
  const confirmEndEmploymentUpdate = useCallback(
    (onSuccess: () => void): void => {
      runConfirmEndEmploymentUpdate({
        companyId: params.companyId,
        employmentRules: params.employmentRules,
        endEmploymentDate: params.endEmploymentDate,
        isSaving: params.isSaving,
        onSuccess,
        onWorkerUpdated: params.onWorkerUpdated,
        profile: params.profile,
        applySavedProfile: params.applySavedProfile,
        reloadProfile: params.reloadProfile,
        setIsSaving: params.setIsSaving,
        setSaveError: params.setSaveError,
        translate: params.translate,
        workerId: params.workerId,
      });
    },
    [params],
  );

  const handleOpenEndEmploymentModal = useCallback((): void => {
    if (params.isSaving) return;

    params.setEndEmploymentDate(
      toSuggestedEndEmploymentDate(params.selectedEmploymentStartDate),
    );
    params.setSaveError("");
    params.setModalState({ type: "endEmployment" });
  }, [params]);

  const openEndEmploymentForCreateModal = useCallback((): void => {
    params.setEndEmploymentDate(
      toSuggestedEndEmploymentDate(params.selectedEmploymentStartDate),
    );
    params.setSaveError("");
    params.setModalState({ type: "endEmploymentForCreate" });
  }, [params]);

  const handleCloseEndEmploymentModal = useCallback((): void => {
    if (params.isSaving) return;
    params.setSaveError("");
    params.setModalState({ type: "none" });
  }, [params]);

  const handleConfirmEndEmploymentModal = useCallback((): void => {
    confirmEndEmploymentUpdate((): void => {
      params.setModalState({ type: "none" });
    });
  }, [confirmEndEmploymentUpdate, params]);

  const handleCloseEndEmploymentForCreateModal = useCallback((): void => {
    if (params.isSaving) return;
    params.setSaveError("");
    params.setModalState({ type: "none" });
  }, [params]);

  const handleConfirmEndEmploymentForCreateModal = useCallback((): void => {
    confirmEndEmploymentUpdate((): void => {
      params.setModalState({ type: "none" });
      params.onStartCreatingEmployment();
    });
  }, [confirmEndEmploymentUpdate, params]);

  return {
    handleOpenEndEmploymentModal,
    openEndEmploymentForCreateModal,
    handleCloseEndEmploymentModal,
    handleConfirmEndEmploymentModal,
    handleCloseEndEmploymentForCreateModal,
    handleConfirmEndEmploymentForCreateModal,
  };
};
